import React, { useState } from 'react';
import { Check, CircleNotch } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = `${window.location.origin}/api`;

const MONTHS_PL = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

/**
 * Button that lets admin mark every payment of a given month as paid across
 * a scope (private / corporate / leasing / all).
 *
 * Props:
 *   scope: "private" | "corporate" | "leasing" | "all"
 *   scopeLabel: display label (e.g. "kredytach prywatnych")
 *   onDone: callback after success (to refresh)
 *   testid: optional data-testid override
 */
export default function MarkMonthPaidButton({
    scope,
    scopeLabel,
    onDone,
    testid,
}) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [busy, setBusy] = useState(false);

    const yearOptions = [];
    for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

    const onClick = async () => {
        const label = `${MONTHS_PL[month - 1]} ${year}`;
        if (!window.confirm(
            `Czy wszystkie raty w ${scopeLabel} za ${label} zostały opłacone?\n\n` +
            `Po potwierdzeniu wszystkie raty z datą w tym miesiącu zostaną oznaczone jako OPŁACONE. ` +
            `Tej operacji nie można cofnąć grupowo — będziesz musiał odznaczyć ratę indywidualnie w edycji kredytu/leasingu.`,
        )) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/obligations/mark-month-paid`, {
                year, month, scope,
            });
            const parts = Object.entries(data.summary || {}).map(([k, v]) => {
                if (v.payments_marked === 0) return null;
                return `${k}: ${v.payments_marked} rat (${v.documents_updated} pozycji)`;
            }).filter(Boolean);
            if (parts.length === 0) {
                alert(`Brak nieopłaconych rat za ${label} w ${scopeLabel}.`);
            } else {
                alert(`Oznaczono jako opłacone za ${label}:\n${parts.join('\n')}`);
            }
            if (onDone) onDone();
        } catch (e) {
            alert('Błąd: ' + (e.response?.data?.detail || e.message));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-lg px-3 py-2">
            <span className="text-sm font-semibold text-emerald-800">
                Czy wszystkie raty zostały opłacone za
            </span>
            <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
                disabled={busy}
                data-testid={`${testid || 'mark-paid'}-month`}
            >
                {MONTHS_PL.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                ))}
            </select>
            <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
                disabled={busy}
                data-testid={`${testid || 'mark-paid'}-year`}
            >
                {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                ))}
            </select>
            <span className="text-sm text-emerald-800">?</span>
            <button
                onClick={onClick}
                disabled={busy}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm font-semibold flex items-center gap-1.5"
                data-testid={testid || 'mark-paid-btn'}
            >
                {busy ? (
                    <CircleNotch className="w-4 h-4 animate-spin" />
                ) : (
                    <Check className="w-4 h-4" weight="bold" />
                )}
                Tak, oznacz jako opłacone
            </button>
        </div>
    );
}
